import './i18n';
import React from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import ConnectedStore from './Shared/ConnectedStore';
import { UserReducer, initialState, Provider as UserProvider } from './Shared/UserContext';
import { HelmetProvider } from "react-helmet-async";
import {
  Provider as ProviderConfig,
  initialState as initConfig,
  ConfigReducer,
} from './Shared/ConfigContext';
import ProtectedRoute from './Shared/ProtectedRoute';

import Login from './components/Login';
import Forbidden from './components/Forbidden'; // <— add a simple 403 page

// modules…
import Clients from './modules/Clients/Clients';
import Products from './modules/Products/Products';
import Utilisateurs from './modules/Users/Utilisateurs';
import ClientDetails from './modules/Clients/ClientDetails';
import ModuleWrapper from './components/ModuleWrapper';
import Brands from './modules/Brands/Brands';
import Families from './modules/Families/Families';
import Orders from './modules/Orders/Orders';
import OrderDetails from './modules/Orders/OrderDetails';
import Repositories from './modules/Repo/Repositories';
import Providers from './modules/Repo/Providers';
import ProductsByRepo from './modules/Repo/ProductsByRepo';
import Purchases from './modules/Purchases/Purchases';
import Transfers from './modules/Repo/Transfers';
import Dashboard from './modules/Dashboard/Dashboard';
import DeliveryNotes from './modules/DeliveryNotes/DeliveryNotes';
import DeliveryNoteDetails from './modules/DeliveryNotes/DeliveryNoteDetails';
import DeliveryNoteForm from './modules/DeliveryNotes/DeliveryNoteForm';
import CreditNotes from './modules/CreditNotes/CreditNotes';
import CreditNoteDetails from './modules/CreditNotes/CreditNoteDetails';
import Invoices from './modules/Invoices/Invoices';
import InvoiceDetails from './modules/Invoices/InvoiceDetails';
import Expenses from './modules/Performance/Expences';
import Quotes from './modules/Quotes/Quotes';
import ExpenseDetails from './modules/Performance/ExpenseDetails';
import Banks from './modules/Banks/Banks';
import PaymentTypes from './modules/PaymentTypes/PaymentsTypes';
import Services from './modules/Products/Services';
import Materiels from './modules/Products/Materiels';
import FichesReception from './modules/FichesReception/FichesReception';
import FicheReceptionDetails from './modules/FichesReception/FicheReceptionDetails';
import Projects from './modules/Projects/Projects';
import ProjectDetails from './modules/Projects/ProjectDetails';
import Kanban from './modules/Kanban/Kanban';
import Calendar from './modules/Calendar/Calendar';
import BrandMake from './modules/BrandMake/BrandMake';
import Insurers from './modules/Insurers/Insurers';
import VehicleModels from './modules/VehicleModels/VehicleModels';
import DocumentCategories from './modules/AttachementDocumentsType/AttachementDocumentsType';
import Propositions from './modules/Propositions/Propositions';
import PropositionDetails from './modules/Propositions/PropositionDetails';

import './index.css';
import './output.css';

// role helpers (from your sidebar matrix)
const ALL = ['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER','ROLE_BILLING_AGENT','ROLE_TECHNICIAN'];
const ADMIN = ['ROLE_ADMIN'];
const ADMIN_SC = ['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT'];
const ADMIN_SC_BILL = ['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_BILLING_AGENT'];
const ADMIN_WM_TECH = ['ROLE_ADMIN','ROLE_WORKSHOP_MANAGER','ROLE_TECHNICIAN'];
const ADMIN_WM_SK = ['ROLE_ADMIN','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER'];
const ADMIN_WM_SK_TECH = ['ROLE_ADMIN','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER','ROLE_TECHNICIAN'];
const ADMIN_TECH = ['ROLE_ADMIN','ROLE_TECHNICIAN'];
const ADMIN_SC_WM_SK = ['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER'];
const ADMIN_SC_WM_BILL_TECH = ['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_BILLING_AGENT','ROLE_TECHNICIAN'];
const ADMIN_SC_WM_SK_BILL_TECH = ALL; 

const AppRoutes = () => {
  return (
    <Switch>
      <Route path="/login" component={Login} exact />
      <Route path="/403" component={Forbidden} exact />

      {/* Dashboard */}
      <ProtectedRoute path="/" exact component={Dashboard} allowedRoles={ALL} />

      {/* Calendar */}
      <ProtectedRoute path="/calendrier" exact component={Calendar} allowedRoles={['ROLE_ADMIN', 'ROLE_SERVICE_CONSULTANT']} />

      {/* Kanban - Technician View (no sidebar) */}
      <ProtectedRoute path="/kanban" exact component={Kanban} allowedRoles={['ROLE_TECHNICIAN']} noLayout={true} />

      {/* Catalog */}
      <ProtectedRoute path="/catalog/brands" exact component={Brands} allowedRoles={ADMIN_SC_WM_SK_BILL_TECH} />
      <ProtectedRoute path="/catalog/families" exact component={Families} allowedRoles={ADMIN_SC_WM_SK_BILL_TECH} />
      <ProtectedRoute path="/catalog/products" exact component={Products} allowedRoles={ADMIN_SC_WM_SK_BILL_TECH} />
      <ProtectedRoute path="/catalog/services" exact component={Services} allowedRoles={ADMIN_SC_WM_SK} />
      <ProtectedRoute path="/catalog/materiels" exact component={Materiels} allowedRoles={ADMIN_SC_WM_SK} />

      {/* Fiche de réception */}
      <ProtectedRoute path="/fiche-reception/details/:id" exact component={FicheReceptionDetails} allowedRoles={['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER','ROLE_BILLING_AGENT']} />
      <ProtectedRoute path="/fiche-reception" exact component={FichesReception} allowedRoles={['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER','ROLE_BILLING_AGENT']} />

      {/* Clients */}
      <ProtectedRoute path="/clients/add" render={(p) => <ModuleWrapper {...p} moduleType="clients" />} allowedRoles={ADMIN_SC_WM_BILL_TECH.filter(r => r !== 'ROLE_TECHNICIAN')} />
      <ProtectedRoute path="/clients/modify/:id" render={(p) => <ModuleWrapper {...p} moduleType="clients" mode="modify" />} allowedRoles={ADMIN_SC_WM_BILL_TECH.filter(r => r !== 'ROLE_TECHNICIAN')} />
      <ProtectedRoute path="/clients/details/modify/:id" render={(p) => <ModuleWrapper {...p} moduleType="clients" mode="modify" />} allowedRoles={ADMIN_SC_WM_BILL_TECH.filter(r => r !== 'ROLE_TECHNICIAN')} />
      <ProtectedRoute path="/clients/details/:id" exact component={ClientDetails} allowedRoles={ADMIN_SC_WM_BILL_TECH.filter(r => r !== 'ROLE_TECHNICIAN')} />
      <ProtectedRoute path="/clients" exact component={Clients} allowedRoles={ADMIN_SC_WM_BILL_TECH.filter(r => r !== 'ROLE_TECHNICIAN')} />

      {/* Projects */}
      <ProtectedRoute path="/projects/details/:id" exact component={ProjectDetails} allowedRoles={ALL.filter(r => r !== 'ROLE_STOREKEEPER')} />
      <ProtectedRoute path="/projects" exact component={Projects} allowedRoles={ALL.filter(r => r !== 'ROLE_STOREKEEPER')} />

      {/* Purchases */}
      <ProtectedRoute path="/purchases/add" render={(p) => <ModuleWrapper {...p} moduleType="purchases" />} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />
      <ProtectedRoute path="/purchases" exact component={Purchases} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />

      {/* Quotes */}
      <ProtectedRoute path="/quotes" component={Quotes} allowedRoles={ADMIN_TECH} />

      {/* Orders */}
      <ProtectedRoute path="/orders/details/:id" exact component={OrderDetails} allowedRoles={['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />
      <ProtectedRoute path="/orders" exact component={Orders} allowedRoles={['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />

      {/* Propositions */}
      <ProtectedRoute path="/propositions/details/:id" exact component={PropositionDetails} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER']} />
      <ProtectedRoute path="/propositions" exact component={Propositions} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER']} />

      {/* Delivery notes */}
      <ProtectedRoute path="/delivery-notes/details/:id" exact component={DeliveryNoteDetails} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />
      <ProtectedRoute path="/delivery-notes/add" exact component={DeliveryNoteForm} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />
      <ProtectedRoute path="/delivery-notes" exact component={DeliveryNotes} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />

      {/* Invoices */}
      <ProtectedRoute path="/invoices/details/:id" exact component={InvoiceDetails} allowedRoles={['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_BILLING_AGENT','ROLE_TECHNICIAN']} />
      <ProtectedRoute path="/invoices" exact component={Invoices} allowedRoles={['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER','ROLE_BILLING_AGENT','ROLE_TECHNICIAN']} />

      {/* Credit notes */}
      <ProtectedRoute path="/credit-notes/details/:id" exact component={CreditNoteDetails} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />
      <ProtectedRoute path="/credit-notes/add-from-delivery-note" exact render={(p) => <ModuleWrapper {...p} moduleType="creditNotesFromDeliveryNote" />} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />
      <ProtectedRoute path="/credit-notes" exact component={CreditNotes} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER','ROLE_TECHNICIAN']} />

      {/* Performance */}
      <ProtectedRoute path="/expenses/add" render={(p) => <ModuleWrapper {...p} moduleType="expenses" />} allowedRoles={['ROLE_BILLING_AGENT']} />
      <ProtectedRoute path="/expenses/details/modify/:id" render={(p) => <ModuleWrapper {...p} moduleType="expenses" mode="modify" />} allowedRoles={['ROLE_BILLING_AGENT']} />
      <ProtectedRoute path="/expenses/modify/:id" render={(p) => <ModuleWrapper {...p} moduleType="expenses" mode="modify" />} allowedRoles={['ROLE_BILLING_AGENT']} />
      <ProtectedRoute path="/expenses/details/:id" exact component={ExpenseDetails} allowedRoles={['ROLE_BILLING_AGENT']} />
      <ProtectedRoute path="/expenses" exact component={Expenses} allowedRoles={['ROLE_BILLING_AGENT']} />

      {/* Repo */}
      <ProtectedRoute path="/products-by-repo" exact component={ProductsByRepo} allowedRoles={['ROLE_ADMIN','ROLE_WORKSHOP_MANAGER', 'ROLE_SERVICE_CONSULTANT', 'ROLE_STOREKEEPER']} />
      <ProtectedRoute path="/transfers/add" exact render={(p) => <ModuleWrapper {...p} moduleType="requests" />} allowedRoles={['ROLE_ADMIN','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER']} />
      <ProtectedRoute path="/transfers/modify/:id" exact render={(p) => <ModuleWrapper {...p} moduleType="requests" mode="modify" />} allowedRoles={['ROLE_ADMIN','ROLE_WORKSHOP_MANAGER','ROLE_STOREKEEPER']} />
      <ProtectedRoute path="/transfers" exact component={Transfers} allowedRoles={ADMIN_WM_SK} />
      <ProtectedRoute path="/repos" exact component={Repositories} allowedRoles={['ROLE_ADMIN','ROLE_WORKSHOP_MANAGER', 'ROLE_SERVICE_CONSULTANT', 'ROLE_STOREKEEPER']} />
      <ProtectedRoute path="/providers" exact component={Providers} allowedRoles={['ROLE_ADMIN','ROLE_STOREKEEPER']} />

      {/* Administration */}
      <ProtectedRoute path="/users" exact component={Utilisateurs} allowedRoles={['ROLE_ADMIN','ROLE_WORKSHOP_MANAGER', 'ROLE_SERVICE_CONSULTANT']} />
      <ProtectedRoute path="/banks" exact component={Banks} allowedRoles={ADMIN_SC_BILL} />
      <ProtectedRoute path="/payment-types" exact component={PaymentTypes} allowedRoles={ADMIN_SC_BILL} />
      <ProtectedRoute path="/vehicle-brands" exact component={BrandMake} allowedRoles={['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER']} />
      <ProtectedRoute path="/insurers" exact component={Insurers} allowedRoles={ADMIN_SC_BILL} />
      <ProtectedRoute path="/vehicle-models" exact component={VehicleModels} allowedRoles={['ROLE_ADMIN','ROLE_SERVICE_CONSULTANT','ROLE_WORKSHOP_MANAGER']} />
      <ProtectedRoute path="/document-categories" exact component={DocumentCategories} allowedRoles={ADMIN_SC_BILL} />
    </Switch>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <div>
          <AppRoutes />
        </div>
      </Router>
    </HelmetProvider>
  );
};

const ConnectedToUserContext = ConnectedStore(App, UserReducer, initialState, UserProvider);
export default ConnectedStore(ConnectedToUserContext, ConfigReducer, initConfig, ProviderConfig);
